/// <reference path="../../typings/browser.d.ts" />

namespace Application.Filters {
    'use strict';
    import Utils = Common.Utils;

    export interface ICustomFilters extends ng.IFilterService {
        (name: 'timezone'): ITimezoneFilter;
    }

    export interface ITimezoneFilter {
        (date: Date, format: string, timezone: string): string;
    }

    export interface IEscapeHtmlFilter {
        (value: string): string;
    }

    export function orderObjectBy(): Object {
        return function(input: Object, attribute: string): Object {
            if (!angular.isObject(input)) return input;

            let array: Object[] = [];
            for (let objectKey in input) {
                if (input.hasOwnProperty(objectKey))
                    array.push(input[objectKey]);
            }
            array.sort(function(a: Object, b: Object): number {
                return (a[attribute] > b[attribute]) ? 1 : ((b[attribute] > a[attribute]) ? -1 : 0);
            });
            return array;
        };
    }

    export function bytesToHumanReadableFilter(): Object {
        return function(input: number, decimals?: number): string {
            decimals = decimals ? decimals : 1;
            if (input !== undefined && typeof(input) === 'number') {
                if (input === 0)
                    return "0 B";
                else if (input > 0)
                    return Common.Utils.humanReadableBytes(input, decimals);
                else if (input < 0)
                    return '-' + Common.Utils.humanReadableBytes(input * -1, decimals);
            }
        };
    }

    export function bitrateToHumanReadableFilter(): Object {
        return function(input: number): string {
            if (input !== undefined && typeof input === 'number') {
                if (input === 0)
                    return "0 bps";
                else if (input > 0)
                    return Common.Utils.bitRateToHumanReadable(input);
                else if (input < 0)
                    return '-' + Common.Utils.bitRateToHumanReadable(input * -1);
            }
        };
    }

    export function remainingHumanReadableAndBadge(): Object {
        return function(input: number): string {
            let lowThreshold: number = 500 * Math.pow(1024, 2); // 500 MB
            let midThreshold: number = Math.pow(1024, 3); // 1 GB
            if (input && typeof(input) === 'number') {
                if (input === 0)
                    return "<span class='uk-badge uk-badge-danger'>0 B</span>";
                else if (input <= lowThreshold)
                    return "<span class='uk-badge uk-badge-warning'>" + Common.Utils.humanReadableBytes(input, 1) + "</span>";
                else if (input >= lowThreshold && input < midThreshold)
                    return "<span class='uk-badge uk-badge-info'>" + Common.Utils.humanReadableBytes(input, 1) + "</span>";
                else
                    return "<span class='uk-badge uk-badge-success'>" + Common.Utils.humanReadableBytes(input, 1) + "</span>";
            }
        };
    }

    export function capitalizeFirstLetterFilter(): Object {
        return function(input: string): string {
            if (input !== undefined && typeof(input) === 'string' && input !== '') {
                input = input.toLowerCase();
                return input.substring(0, 1).toUpperCase() + input.substring(1);
            }
        };
    }

    export function fromEpochToReadableDate(): Object {
        return function(secs: number): string {
            let newDate: Date = new Date(0); // epoch
            newDate.setSeconds(secs);
            return newDate.getFullYear() + '/' + (newDate.getMonth() + 1) + '/' + newDate.getDate() + 
            ' ' + newDate.toLocaleTimeString();
        };
    }

    export function secsToHHMMSS(): Object {
        return function(secs: number): string {
            let hours: number = Math.floor(secs / (60 * 60));
            let divisorForMinutes: number = secs % (60 * 60);
            let minutes: number = Math.floor(divisorForMinutes / 60);

            let divisorForSeconds: number = divisorForMinutes % 60;
            let seconds: number = Math.ceil(divisorForSeconds);

            if (minutes === 0) {
                return seconds + " s";
            } else if (hours === 0) {
                return minutes + " m : " + seconds + " s";
            } else {
                return hours + " h : " + minutes + " m : " + seconds + " s";
            }
        };
    }

    export function startFromFilter(): Object {
        return function(input: Object[], start: number): Object[] {
            start = +start; // parse to int
            return input ? input.slice(start) : [];
        };
    }

    export function timezoneFilter(): Object {
        return function(date: string, format: string, timezone: string): string {
            let jsUTCDateFormat: string = 'ddd, DD MMM YYYY HH:mm:ss GMT';
            if (date === undefined) {
                return '';
            } else {
                let dateToPrint: moment.Moment = moment.utc((new Date(date)).toUTCString(), jsUTCDateFormat);
                if (timezone !== undefined)
                    dateToPrint = dateToPrint.tz(timezone);
                return (format !== undefined) ? dateToPrint.format(format) : dateToPrint.format();
            }
        };
    }

    export function epochTimezoneFilter(): Object {
        return function(epochDate: number, format: string, timezone: string): string {
            if (epochDate === undefined) {
                return '';
            } else {
                let date: Date = new Date(0);
                date.setSeconds(epochDate);
                let dateToPrint: moment.Moment = moment(date);
                if (timezone !== undefined)
                    dateToPrint = dateToPrint.tz(timezone);
                return (format !== undefined) ? dateToPrint.format(format) : dateToPrint.format();
            }
        };
    }

    export function escapeHtml(): Object {
        let entityMap: Object = {
            "&": "&amp;",
            "<": "&lt;",
            ">": "&gt;",
            '"': '&quot;',
            "'": '&#39;',
            "/": '&#x2F;',
        };
        return function(str: string): string {
            return String(str).replace(/[&<>"'\/]/g, function (char: string): string {
                return entityMap[char];
            });
        };
    }

    export function highlightMatchesFilter($sce: ng.ISCEService): Object {
        return function(str: string, searchStr: string): string {
            if (str === undefined || str === '' || searchStr === undefined || searchStr === '')
                return str;
            let regExp: RegExp = new RegExp(searchStr, "ig");
            let match: RegExpExecArray = regExp.exec(str);
            let strCpy: string = str;
            let shift: number = 0;
            while (match) {
                let replacement: string = `<span style="background-color: yellow;">${ match[0] }</span>`;
                let matchLength: number = match[0].length;
                strCpy = strCpy.substr(0, match.index + shift) + replacement + strCpy.substr(match.index + shift + matchLength);
                shift += replacement.length - matchLength;
                match = regExp.exec(str);
            }
            return $sce.trustAsHtml(strCpy);
        };
    }
    highlightMatchesFilter.$inject = ['$sce'];
}